import { MutableRefObject, useRef, useState } from "react"
import { motion } from "framer-motion"
import { BsXCircle } from "react-icons/bs";
import { BsCheckCircle } from "react-icons/bs";
import { BsQuestionCircle } from "react-icons/bs";
import { HxMapGroup } from "../../../Comps/HxMap/HxMapGroup";
import { HexMap } from "../../../Data/HexMap/HexMap";
import { HexMapBuilderParams, BuildHexMap } from "../../../Data/HexMap/HexMapBuilder";
import { HexMapModifier } from "../../../Data/HexMap/HexMapModifier";
import { Note } from "../../../Data/HexMap/Note";
import { ClassNames } from "../../../Util/ClassNames";
import { All } from "../../../Util/Constants/Quantities";
import { TransformTranslate } from "../../../Util/Transform/Transform";
import { PositionSelector } from "../../../Util/PositionSelector/PositionSelector";
import { OutlineBox } from "../../../Comps/OutlineBox/OutlineBox";
import { ExerciseMessage } from "../Utils/Message/ExerciseMessage";
import { Music } from "../../../Util/Music/Music";
import { NotePlayer } from "../../../Sound/NotePlayer";
import { useNotePlayer } from "../../../Sound/SoundStore";

export function ChainOfFifthsExercise(){
    const tonic:number = Music.C
    const notePlayer:MutableRefObject<NotePlayer> = useRef(useNotePlayer())
    const maxSelectionHistory:number = 4
    const radius:number = 130
    const hexWidth:number = radius * 2.8
    const hexHeight:number = radius * 2.8
    const chainOfFifths:number[] = [3,0,4,1,5,2,6]
    const getAnswer = () => {
        const selectedPositionIndex:number = chainOfFifths.indexOf(selectedPosition)
        const answerIndex:number = selectedPositionIndex + 1
        const answer:number = chainOfFifths[answerIndex]
        return answer
    }
    const positionSelector:MutableRefObject<PositionSelector> = useRef(new PositionSelector(maxSelectionHistory))
    const getRandomPosition = (lastIndex:number) => {
        const randomPosition:number = positionSelector.current.getRandomPosition(lastIndex + 1, true) // 1-6
        const adjustedRandomPosition:number = randomPosition - 1 // 0-5
        return adjustedRandomPosition
    }
    const [selectedPosition, setSelectedPosition] = useState(getRandomPosition(-2))
    const answerStatus:MutableRefObject<boolean|null> =  useRef(null)
    const [answerPosition, setAnswerPosition] = useState<number|null>(null)
    const getMessage = () => {
        if(answerStatus.current === null){
            return `Find the fifth of the ${selectedPosition + 1} position.`
        }
        if(answerStatus.current === true){
            return `Correct!`
        }
        return `Incorrect!`
    }
    const getNextMessage = (position:number) => {
        return `Find the fifth of the ${position + 1} position.`
    }
    const getInitialMessage = () => {
        return getMessage()
    }
    const [message, setMessage] = useState(getInitialMessage())
    const nextPosition = () => {
        answerStatus.current = null
        setAnswerPosition(null)
        const position:number = getRandomPosition(selectedPosition)
        setMessage(getNextMessage(position))
        setSelectedPosition(position)
    }
    const onCorrect = (note:Note) => {
        answerStatus.current = true
        setMessage('Correct!')
        const noteTonic:number = Music.getKeyScale(tonic)[selectedPosition]
        notePlayer.current.playNotes([noteTonic, note.params.tonic], true, 500)
        setAnswerPosition(note.params.noteIndex)
        setTimeout(nextPosition, 1000)

    }
    const resetAfterIncorrect = () => {
        answerStatus.current = null
        setMessage(getNextMessage(selectedPosition))
    }
    const onIncorrect = () => {
        answerStatus.current = false
        setMessage('Incorrect. Try again.')
        notePlayer.current.playBuzzer()
        setTimeout(resetAfterIncorrect, 2000)
    }
    const onNoteClick = (note:Note) => {
        const answer:number = getAnswer()
        if(note.params.noteIndex === answer){
            onCorrect(note)
            return
        }
        onIncorrect()
    }
    const onKeyClick = () => {}
    const getMap = () => {
        const params:HexMapBuilderParams = {
            rows:1, columns:1, tonic:tonic, radius:radius
        }
        const map:HexMap = BuildHexMap(params)
        const mod:HexMapModifier = new HexMapModifier(map)
        mod.notes_setPrimaryLabelAsPosition([All])
        mod.notes_setSelected([selectedPosition])
        if(answerPosition !== null){
            mod.notes_setHilited([answerPosition])
        }
        return map
    }

    return(
        <OutlineBox title={'Chain of Fifths Exercise'} width={600}>
            <div className={'hex-pattern'}>
                <ExerciseMessage key={Date.now()} message={message} answerStatus={answerStatus.current}/>
                <div>
                    <svg width={hexWidth} height={hexHeight}>
                        <g transform={TransformTranslate(hexWidth/2,hexHeight/2)}>
                            <HxMapGroup onNoteClick={onNoteClick} onKeyClick={onKeyClick} hexMap={getMap()}>
                            </HxMapGroup>
                        </g>
                    </svg>
                </div>
            </div>
        </OutlineBox>
        
    )
}
function Message(props:{message:string, correct:boolean|null}){
    const scale:number = 0
    const animateScale:number = 1
    const getClassNames = () => {
        const classNames:ClassNames = new ClassNames(['exercise-message'])
        if(props.correct !== null){ classNames.addClass((props.correct) ? 'correct' : 'incorrect') }
        return classNames.toString()
    }
    const getIcon = ():React.ReactNode => {
        if(props.correct === null){return <BsQuestionCircle/>}
        return(props.correct) ? <BsCheckCircle/> : <BsXCircle />
    }
    return(
        <div className='centered'>
            <motion.div initial={{scale:scale}} animate={{scale:animateScale}} transition={{duration:0.4}} className={getClassNames()}>
                <div className='container'><span>{props.message}</span>{getIcon()}</div>
            </motion.div>
        </div> 
    )
}